if (!customElements.get('ca-size-guide')) {
  customElements.define('ca-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.productRoot = this.closest('product-info');
      this.dialog = this.querySelector('dialog');
      if (!this.productRoot || !this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.onTrigger = (event) => {
        const trigger = event.target.closest('[data-ca-size-guide-trigger]');
        if (!trigger || trigger.closest('product-info') !== this.productRoot) return;
        if (!this.populateTables()) return;
        event.preventDefault();
        this.open(trigger);
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Tab') return;
        const stops = [...this.dialog.querySelectorAll('button, [href], [tabindex="0"]')].filter((node) => !node.disabled && node.getClientRects().length);
        const first = stops[0], last = stops[stops.length - 1];
        if (!first) { event.preventDefault(); this.dialog.focus(); return; }
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      };
      this.onClose = () => this.restorePage();
      this.onCancel = (event) => { event.preventDefault(); this.dialog.close(); };
      this.onBackdropDown = (event) => { this.backdropDown = this.isBackdrop(event); };
      this.onBackdropUp = (event) => { if (this.backdropDown && this.isBackdrop(event)) this.dialog.close(); this.backdropDown = false; };
      this.onCloseButton = () => this.dialog.close();
      document.addEventListener('click', this.onTrigger);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('pointerdown', this.onBackdropDown);
      this.dialog.addEventListener('pointerup', this.onBackdropUp);
      this.querySelector('[data-ca-size-guide-close]').addEventListener('click', this.onCloseButton);
      this.refreshTriggers = () => {
        const hasTables = !!this.productRoot.querySelector('[data-ca-size-guide-source] table');
        this.productRoot.querySelectorAll('[data-ca-size-guide-trigger]').forEach((trigger) => { trigger.hidden = !hasTables; });
      };
      this.observer = new MutationObserver(this.refreshTriggers);
      this.observer.observe(this.productRoot, { childList: true, subtree: true });
      this.refreshTriggers();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      this.observer?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.restorePage();
    }
    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const box = this.dialog.getBoundingClientRect();
      return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
    }
    populateTables() {
      const tables = this.productRoot.querySelectorAll('[data-ca-size-guide-source] table');
      if (!tables.length) return false;
      const content = this.querySelector('[data-ca-size-guide-tables]');
      content.replaceChildren();
      tables.forEach((table, index) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'ca-size-guide-table-scroll';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', 'Product measurements — scroll horizontally for all sizes');
        let preceding = table.previousElementSibling;
        if (!preceding) preceding = table.parentElement?.previousElementSibling;
        const isHeading = preceding && /^H[1-6]$/.test(preceding.tagName);
        const isUnitLabel = preceding?.tagName === 'P' && preceding.textContent.length < 80 && /inches|centimet(?:er|re)s|\bcm\b|imperial|metric/i.test(preceding.textContent);
        if (isHeading || isUnitLabel) {
          const heading = document.createElement('h3');
          heading.textContent = preceding.textContent;
          content.append(heading);
        }
        const clone = table.cloneNode(true);
        const idMap = new Map();
        [clone, ...clone.querySelectorAll('[id]')].forEach((node) => {
          if (!node.id) return;
          const oldId = node.id;
          node.id = this.dialog.id + '-table-' + index + '-' + oldId;
          idMap.set(oldId, node.id);
        });
        clone.querySelectorAll('[headers], [aria-labelledby], [aria-describedby]').forEach((node) => {
          ['headers', 'aria-labelledby', 'aria-describedby'].forEach((attribute) => {
            if (!node.hasAttribute(attribute)) return;
            node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map((id) => idMap.get(id) || id).join(' '));
          });
        });
        wrapper.append(clone);
        content.append(wrapper);
      });
      return true;
    }
    open(trigger) {
      if (this.dialog.open) return;
      this.returnFocus = trigger;
      this.scrollPosition = window.scrollY;
      this.bodyStyle = document.body.getAttribute('style');
      this.htmlOverflow = document.documentElement.style.overflow;
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      const bodyPadding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -this.scrollPosition + 'px';
      document.body.style.width = '100%';
      document.body.style.paddingRight = bodyPadding + scrollbar + 'px';
      document.documentElement.style.overflow = 'hidden';
      this.pageLocked = true;
      this.dialog.showModal();
      this.querySelector('[data-ca-size-guide-close]').focus({ preventScroll: true });
    }
    restorePage() {
      if (!this.pageLocked) return;
      this.pageLocked = false;
      if (this.bodyStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', this.bodyStyle);
      document.documentElement.style.overflow = this.htmlOverflow;
      const trigger = this.returnFocus?.isConnected ? this.returnFocus : this.productRoot.querySelector('[data-ca-size-guide-trigger]');
      trigger?.focus({ preventScroll: true });
      window.scrollTo({ top: this.scrollPosition, left: window.scrollX, behavior: 'instant' });
    }
  });
}

